import { AlertCircle, ArrowLeft, CheckCircle, File, Upload, RefreshCw } from 'lucide-react';
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../components/Footer';
import { Navbar } from '../components/Navbar';
import { usePublicConfig } from '../contexts/usePublicConfig';
import { useSocket } from '../contexts/socketTypes';
import { analytics } from '../services/analytics/posthog';
import { useZipUpload, formatFileSize } from '../hooks/useZipUpload';

export const ZipUploadPage: React.FC = () => {
  const { socket } = useSocket();
  const publicConfig = usePublicConfig();
  const maxUploadSizeMB = publicConfig.maxUploadSizeMB ?? 500;

  const {
    selectedFile,
    isUploading,
    uploadProgress,
    uploadStatus,
    error,
    handleFileSelect,
    handleDrop,
    handleDragOver,
    handleUpload,
    handleCancelUpload,
    clearSelectedFile,
  } = useZipUpload(maxUploadSizeMB, { socketId: socket?.id });

  useEffect(() => {
    analytics.pageViewed('zip_upload');
  }, []);

  return (
    <div className="min-h-screen flex flex-col transition-colors duration-200 px-4 sm:px-6 lg:px-8">
      <Navbar pageTitle="Upload Project" showDivider={true} />

      <main className="grow mt-16">
        <div className="max-w-7xl mx-auto py-8">
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-2 mb-6 text-sm font-medium transition-colors duration-200"
            style={{ color: 'var(--color-text-secondary)' }}
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </Link>

          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold mb-2" style={{ color: 'var(--color-text-primary)' }}>
              Upload Your Project
            </h1>
            <p style={{ color: 'var(--color-text-secondary)' }}>
              Upload your project as an archive file to get started with analysis
            </p>
          </div>

          <div className="mb-8">
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-all duration-200 ${
                selectedFile
                  ? 'border-green-300 bg-green-50'
                  : 'border-gray-300 hover:border-gray-400'
              }`}
              style={{
                borderColor: selectedFile
                  ? 'var(--color-accent-green)'
                  : 'var(--color-border-default)',
                backgroundColor: selectedFile
                  ? 'var(--color-bg-success)'
                  : 'var(--color-bg-secondary)',
              }}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              {selectedFile ? (
                <div className="space-y-4">
                  <CheckCircle
                    className="w-12 h-12 mx-auto"
                    style={{ color: 'var(--color-accent-green)' }}
                  />
                  <div>
                    <h4 className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                      File Selected
                    </h4>
                    <div
                      className="flex items-center justify-center gap-2 mt-2 text-sm"
                      style={{ color: 'var(--color-text-secondary)' }}
                    >
                      <File className="w-4 h-4" />
                      <span>{selectedFile.name}</span>
                      <span>({formatFileSize(selectedFile.size)})</span>
                    </div>
                  </div>
                  <button
                    onClick={clearSelectedFile}
                    className="text-sm underline"
                    style={{ color: 'var(--color-text-tertiary)' }}
                  >
                    Choose different file
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  <Upload
                    className="w-12 h-12 mx-auto"
                    style={{ color: 'var(--color-text-tertiary)' }}
                  />
                  <div>
                    <h4
                      className="font-semibold mb-2"
                      style={{ color: 'var(--color-text-primary)' }}
                    >
                      Drop your archive file here
                    </h4>
                    <p className="text-sm mb-4" style={{ color: 'var(--color-text-secondary)' }}>
                      Or click to browse and select a file
                    </p>

                    <input
                      type="file"
                      accept=".zip,.tar,.tar.gz,.tgz,.tar.bz2,.tbz2,.rar,.7z,.gz,.bz2"
                      onChange={handleFileSelect}
                      className="hidden"
                      id="zip-upload"
                    />
                    <label
                      htmlFor="zip-upload"
                      className="inline-flex items-center gap-2 px-6 py-3 rounded-lg font-medium cursor-pointer transition-all duration-200 hover:shadow-md"
                      style={{
                        backgroundColor: 'var(--color-accent-teal)',
                        color: 'white',
                      }}
                    >
                      <Upload className="w-4 h-4" />
                      Choose File
                    </label>
                  </div>
                  <p className="text-xs" style={{ color: 'var(--color-text-tertiary)' }}>
                    Maximum file size: {maxUploadSizeMB}MB • Supports ZIP, TAR, TAR.GZ, RAR, 7Z, and
                    more
                  </p>
                </div>
              )}
            </div>

            {error && (
              <div className="mt-4 p-4 rounded-lg border border-red-200 bg-red-50 flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-red-500 shrink-0" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}
          </div>

          {selectedFile && (
            <div className="text-center">
              {isUploading ? (
                <div className="space-y-4">
                  <div
                    className="w-full rounded-full h-3 overflow-hidden"
                    style={{ backgroundColor: 'var(--color-bg-tertiary)' }}
                  >
                    <div
                      className="h-3 rounded-full transition-all duration-300 relative"
                      style={{
                        width: `${uploadProgress}%`,
                        backgroundColor: 'var(--color-accent-teal)',
                      }}
                    >
                      {uploadProgress > 0 && (
                        <div className="absolute inset-0 bg-white opacity-20 animate-pulse" />
                      )}
                    </div>
                  </div>
                  <div className="text-center">
                    <p
                      className="text-sm font-medium"
                      style={{ color: 'var(--color-text-primary)' }}
                    >
                      {uploadStatus || `Uploading... ${uploadProgress}%`}
                    </p>
                    {selectedFile && uploadProgress > 0 && uploadProgress < 100 && (
                      <p
                        className="text-xs mt-1 flex items-center justify-center gap-1"
                        style={{ color: 'var(--color-text-secondary)' }}
                      >
                        <RefreshCw className="w-3 h-3 animate-spin" />
                        {formatFileSize(
                          Math.min((selectedFile.size * uploadProgress) / 100, selectedFile.size),
                        )}{' '}
                        of {formatFileSize(selectedFile.size)}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={handleCancelUpload}
                    className="px-4 py-2 text-sm rounded-lg border transition-colors hover:bg-red-50 dark:hover:bg-red-900/20"
                    style={{
                      borderColor: 'var(--color-border-default)',
                      color: 'var(--color-text-secondary)',
                    }}
                  >
                    Cancel Upload
                  </button>
                </div>
              ) : (
                <button
                  onClick={handleUpload}
                  className="px-8 py-3 rounded-lg font-semibold transition-all duration-200 hover:shadow-md"
                  style={{
                    backgroundColor: 'var(--color-accent-teal)',
                    color: 'white',
                  }}
                >
                  Upload and Analyze
                </button>
              )}
            </div>
          )}

          <div
            className="mt-12 p-6 rounded-lg border"
            style={{
              backgroundColor: 'var(--color-bg-secondary)',
              borderColor: 'var(--color-border-default)',
            }}
          >
            <h3 className="font-semibold mb-3" style={{ color: 'var(--color-text-primary)' }}>
              What happens next?
            </h3>
            <ul className="space-y-2 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              <li className="flex items-start gap-2">
                <div
                  className="w-1.5 h-1.5 rounded-full mt-2 shrink-0"
                  style={{ backgroundColor: 'var(--color-accent-teal)' }}
                />
                Your file is uploaded in small chunks for reliable transfers (resumable if
                interrupted)
              </li>
              <li className="flex items-start gap-2">
                <div
                  className="w-1.5 h-1.5 rounded-full mt-2 shrink-0"
                  style={{ backgroundColor: 'var(--color-accent-teal)' }}
                />
                We'll extract and analyze your project structure and dependencies
              </li>
              <li className="flex items-start gap-2">
                <div
                  className="w-1.5 h-1.5 rounded-full mt-2 shrink-0"
                  style={{ backgroundColor: 'var(--color-accent-teal)' }}
                />
                You'll get comprehensive insights and recommendations for your codebase
              </li>
            </ul>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};
